import {
  Component,
  inject,
  OnInit,
  OnDestroy,
  PLATFORM_ID,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  ViewChild,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { PageEvent } from '@angular/material/paginator';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTableModule } from '@angular/material/table';
import { NgbDropdown, NgbModal, NgbModalOptions, NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { MomentModule } from 'ngx-moment';
import { EMPTY, Observable, Subject } from 'rxjs';
import { expand, finalize, reduce, take, takeUntil } from 'rxjs/operators';
import { ActivityStatus } from '../../../core/enum/activity-status.enum';
import { EkycStatus } from '../../../core/enum/ekyc-status.enum';
import { serializeRequiredSections } from '../../profile-review-attest/required-section';
import { CreditFlow } from '../../../core/enum/mysec/credit-flow.enum';
import { WalletTransactionEvent } from '../../../core/enum/mysec/wallet-transaction-event.enum';
import { WalletTransactionStatus } from '../../../core/enum/mysec/wallet-transaction-status.enum';
import { PaymentType } from '../../../core/enum/payment-type.enum';
import { TransactionList } from '../../../core/models/mysec/transaction-list';
import { Wallet } from '../../../core/models/mysec/wallet';
import { PaymentMethod } from '../../../core/models/payment/payment-method';
import { AccountService } from '../../../core/services/api/account.service';
import { WalletService } from '../../../core/services/api/mysec/wallet.service';
import { ErrorService } from '../../../core/services/error.service';
import { BlockUiService } from '../../../shared/services/blockUi/block-ui.service';
import { PopupService } from '../../../shared/services/popup/popup.service';
import { DepositComponent } from './deposit/deposit.component';
import { WalletTransactionFilter } from './wallet-transaction-filter';
import { StatusFilterOption } from './wallet-status-filter-option';
import { WithdrawComponent } from './withdraw/withdraw.component';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { FaqAccordion } from '../../../core/models/learn/faq-accordion';
import { walletFaqLd, ldToList } from '../../../../assets/static/json-ld.js';
import { PstxAccountService } from '../../../core/services/pstx-account.service';
import { DATE_FORMATS } from '../../../core/services/constant';
import { environment } from '../../../../environments/environment';
import { ShareMarketStatus } from '../../../core/enum/mysec/share-market-status.enum';
import { OrderSummaryComponent } from './order-summary/order-summary.component';
import { HeldOpenOrder } from '../../../core/models/mysec/held-open-order';
import { HeldEcfInvestment } from '../../../core/models/mysec/held-ecf-investment';
import { HoldBreakdownData } from './hold-breakdown/hold-breakdown.model';
import { HoldBreakdownSheetComponent } from './hold-breakdown/hold-breakdown-sheet.component';
import { StorageService } from '../../../core/services/storage.service';
import { Role } from '../../../core/enum/role.enum';
import { Clipboard } from '@angular/cdk/clipboard';
import { ToastService } from '../../../core/services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { SharedModule } from '../../../shared/shared.module';

@Component({
  selector: 'app-wallet',
  templateUrl: './wallet.component.html',
  styleUrls: ['./wallet.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    NgbModule,
    TranslateModule,
    MomentModule,
    MatTableModule,
    MatIconModule,
    MatTooltipModule,
    SharedModule,
  ],
})
export class WalletComponent implements OnInit, OnDestroy {
  @ViewChild('transactionTable') transactionTable?: ElementRef<HTMLElement>;

  // #124: the two Balance-on-Hold bucket popovers (desktop). Used to dismiss the
  // open one when the in-popover close (×) is clicked.
  @ViewChildren(NgbPopover) holdPopovers: QueryList<NgbPopover>;

  // Desktop: Event/Status filter triggers live inside their own mat-header-cell
  // (per Figma — anchored to the column header, not a separate "More Filters"
  // button). Each matHeaderCellDef is its own embedded view, so a template ref
  // declared in one isn't visible in the other — hence ViewChild here instead of
  // the template-ref pattern the mobile triggers use below.
  @ViewChild('eventHeaderFilterDropdown') eventHeaderFilterDropdown?: NgbDropdown;
  @ViewChild('statusHeaderFilterDropdown') statusHeaderFilterDropdown?: NgbDropdown;

  @Input() tickerCode: string;
  @Output() onTickerCodeClick = new EventEmitter();

  params: WalletTransactionFilter = new WalletTransactionFilter();
  displayColumns: string[] = ['CreatedDate', 'Event', 'Amount', 'TickerCode', 'Status', 'Action'];
  total: number;

  isLoading = true;
  isWalletLoading = true;
  isTransactionListLoading = true;

  transactionList: TransactionList[];

  wallet: Wallet;
  isWalletFound: boolean = true;

  CreditFlow = CreditFlow;
  WalletTransactionEvent = WalletTransactionEvent;
  ActivityStatus = ActivityStatus;
  WalletTransactionStatus = WalletTransactionStatus;
  paymentMethod: PaymentMethod;
  isMaster = false;
  PaymentType = PaymentType;

  /** ?withdraw=true deep-link: fires withdrawValidation() once wallet + payment method both load. */
  private withdrawDeepLinkRequested = false;
  private withdrawDeepLinkOpened = false;

  StatusFilterOption = StatusFilterOption;

  /** Desktop "More Filters" dropdown — Event filters section (checkboxes). */
  readonly eventFilterOptions: { key: WalletTransactionEvent; labelKey: string }[] = [
    { key: WalletTransactionEvent.Withdraw, labelKey: 'mysec.WithdrawalEvent' },
    { key: WalletTransactionEvent.TopUp, labelKey: 'mysec.TopUp' },
    { key: WalletTransactionEvent.BuyShares, labelKey: 'mysec.BuyShares' },
    { key: WalletTransactionEvent.SellShares, labelKey: 'mysec.SellShares' },
  ];

  /** Desktop "More Filters" dropdown — Status filters section (checkboxes). */
  readonly statusFilterOptions: { key: StatusFilterOption; labelKey: string }[] = [
    { key: StatusFilterOption.Pending, labelKey: 'mysec.PendingApproval' },
    { key: StatusFilterOption.Approved, labelKey: 'mysec.Approved' },
    { key: StatusFilterOption.Completed, labelKey: 'mysec.Completed' },
    { key: StatusFilterOption.Failed, labelKey: 'mysec.Failed' },
  ];

  /** Buy/Sell are the only events a "Completed" (settled trade) status can match. */
  private static readonly TRADE_EVENTS = [
    WalletTransactionEvent.BuyShares,
    WalletTransactionEvent.SellShares,
  ];

  /**
   * Chunk size used to page through the FULL matching set for the ambiguous
   * Approved-vs-Completed case (see `fetchAllMatching`/`getWalletTransaction`).
   * Not a cap — `fetchAllMatching` keeps requesting further chunks until the
   * server's `total` is fully covered, however large an investor's history is.
   */
  private static readonly AMBIGUOUS_FILTER_CHUNK_SIZE = 1000;

  // Working (unapplied) checkbox selections while the dropdown is open.
  draftEventFilters: WalletTransactionEvent[] = [WalletTransactionEvent.All];
  draftStatusFilters: StatusFilterOption[] = [StatusFilterOption.All];

  // Last-applied status selection (business-level; `params.walletTransactionEvents`
  // already doubles as the applied event selection, same as the old status pills did).
  appliedStatusFilters: StatusFilterOption[] = [StatusFilterOption.All];

  walletFaqLd = walletFaqLd;
  pageFaqs: FaqAccordion[] = ldToList(walletFaqLd);
  private readonly destroy$ = new Subject<void>();

  dateFormat: string = DATE_FORMATS.defaultDate;
  timeFormat: string = DATE_FORMATS.defaultTime;

  ShareMarketStatus: typeof ShareMarketStatus = ShareMarketStatus;

  // #124: Balance-on-Hold drill-down state.
  // Viewport < this width => mobile bottom sheet; otherwise desktop anchored popover.
  // 768px matches Bootstrap's md breakpoint (the col-md-6 split of the two cards).
  private static readonly MOBILE_BREAKPOINT = 768;

  /** Open Orders bucket: fetched lazily from the #122 endpoint on first drill. */
  heldOpenOrders: HeldOpenOrder[] | null = null;
  isHeldOpenOrdersLoading = false;
  isPendingWithdrawalsLoading = false;

  /** ECF investment bucket: fetched lazily from heldEcfInvestments on first drill. */
  heldEcfInvestments: HeldEcfInvestment[] | null = null;
  isHeldEcfInvestmentsLoading = false;

  /** The view-model handed to the shared content for the currently-open desktop popover. */
  activeBreakdown: HoldBreakdownData | null = null;

  private readonly errorService = inject(ErrorService);
  private readonly walletService = inject(WalletService);
  private readonly modalService = inject(NgbModal);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly popupService = inject(PopupService);
  private readonly accountService = inject(AccountService);
  private readonly blockUiService = inject(BlockUiService);
  private readonly matIconRegistry = inject(MatIconRegistry);
  private readonly domSanitizer = inject(DomSanitizer);
  private readonly pstxAccountService = inject(PstxAccountService);
  private readonly storgeService = inject(StorageService);
  private readonly clipboard = inject(Clipboard);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly platform: Object = inject(PLATFORM_ID);

  ngOnInit() {
    this.getProfileInfo();
    this.getProfilePaymentMethods();

    if (isPlatformBrowser(this.platform)) {
      this.generateIcons();
    }

    this.route.queryParams.subscribe((params) => {
      if (params.topup === 'true') {
        this.deposit();
      }
      if (params.withdraw === 'true') {
        this.withdrawDeepLinkRequested = true;
        this.tryOpenWithdrawDeepLink();
      }
    });

    this.buildPageFaqs();
    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => this.buildPageFaqs());
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Rebuilds walletFaqLd's mainEntity text from translations (same JSON-LD shape as the
   * original hardcoded data), then feeds it through the same ldToList() used before —
   * re-run on every language switch since the accordion renders plain strings via
   * [innerHTML] rather than the translate pipe.
   *
   * Uses translate.get() rather than instant(): on a direct page load, initLanguage()'s
   * translate.use() may still be loading the translation file, and instant() would write
   * the raw mysec.walletFaq.* keys into the FAQ (and its JSON-LD) until onLangChange fires.
   * get() waits for translations to be ready — emitting synchronously once they already are,
   * which is always true by the time onLangChange fires a subsequent switch — so the field
   * initializer's static English ldToList(walletFaqLd) stays on screen until the first
   * resolution instead of a flash of untranslated keys. */
  private buildPageFaqs(): void {
    const params = { ieoBaseUrl: environment.ieoBaseUrl };
    const faqKeys: Array<[string, string]> = [
      ['accountPurposeQuestion', 'accountPurposeAnswer'],
      ['feeQuestion', 'feeAnswer'],
      ['withdrawalDurationQuestion', 'withdrawalDurationAnswer'],
      ['transactionDeclinedQuestion', 'transactionDeclinedAnswer'],
      ['topUpNotReflectedQuestion', 'topUpNotReflectedAnswer'],
    ];
    const allKeys = faqKeys.flatMap(([q, a]) => [`mysec.walletFaq.${q}`, `mysec.walletFaq.${a}`]);

    this.translateService
      .get(allKeys, params)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((translations) => {
        this.walletFaqLd = {
          ...walletFaqLd,
          mainEntity: faqKeys.map(([questionKey, answerKey]) => ({
            '@type': 'Question',
            name: translations[`mysec.walletFaq.${questionKey}`],
            acceptedAnswer: {
              '@type': 'Answer',
              text: translations[`mysec.walletFaq.${answerKey}`],
            },
          })),
        };
        // ldToList() always sets `open: false` on rebuild; carry forward whichever items
        // the user already had expanded so a language switch doesn't collapse them.
        const openIds = new Set(this.pageFaqs.filter((f) => f.open).map((f) => f.id));
        this.pageFaqs = ldToList(this.walletFaqLd).map((f) => ({ ...f, open: openIds.has(f.id) }));
      });
  }

  /** Fires withdrawValidation() exactly once ?withdraw=true is requested AND both
   * the wallet and payment method have finished loading, then strips the query param. */
  private tryOpenWithdrawDeepLink(): void {
    if (!this.withdrawDeepLinkRequested || this.withdrawDeepLinkOpened) {
      return;
    }

    if (this.isWalletLoading || this.isLoading) {
      return;
    }

    if (!this.wallet || !this.paymentMethod) {
      // Wallet or payment-method load failed while a withdraw deep-link was pending.
      // getWallet()'s error handler already surfaces a toast for generic errors; either
      // way, don't retry forever on refresh — clear the request and strip the param.
      this.withdrawDeepLinkRequested = false;
      if (this.route.snapshot.queryParams.withdraw) {
        this.router.navigate([], { queryParams: {}, replaceUrl: true });
      }
      return;
    }

    this.withdrawDeepLinkOpened = true;
    this.withdrawValidation();
  }

  private clearWithdrawDeepLinkQueryParam(): void {
    if (this.route.snapshot.queryParams.withdraw) {
      this.router.navigate([], { queryParams: {}, replaceUrl: true });
    }
  }

  private generateIcons(): void {
    this.matIconRegistry.addSvgIcon(
      'icon-credit',
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        '../../../../assets/img/icons/svgs/deposit.svg',
      ),
    );

    this.matIconRegistry.addSvgIcon(
      'icon-debit',
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        '../../../../assets/img/icons/svgs/withdraw.svg',
      ),
    );
  }

  getWallet(): void {
    this.isWalletLoading = true;

    this.walletService
      .getWallet()
      .pipe(
        finalize(() => {
          this.isWalletLoading = false;
          this.tryOpenWithdrawDeepLink();
        }),
      )
      .subscribe({
        next: (data) => {
          this.pstxAccountService.setWallet(data);
          this.wallet = data;
          // #124: the bucket subtotals may have changed (e.g. after a cancel),
          // so drop the cached drill-down lists — they refetch on next open.
          this.heldOpenOrders = null;
          this.heldEcfInvestments = null;

          if (this.isMaster) {
            this.getWalletTransaction();
          }
        },
        error: (err) => {
          if (err.error && err.error.code == 'ERR_WALLET_NOT_FOUND') {
            this.pstxAccountService.setIsWalletFound(false);
            this.isWalletFound = false;
          } else {
            this.errorService.showError(err);
          }
        },
      });
  }

  resetPagination(): void {
    this.params.currentPage = 1;
    this.getWalletTransaction(true);
  }

  getWalletTransaction(scrollToView: boolean = false): void {
    this.isTransactionListLoading = true;

    const events = this.params.walletTransactionEvents ?? [WalletTransactionEvent.All];

    // "Completed" and "Approved" resolve to the identical raw status codes (Approved,
    // FPXSuccessful) — see `resolveStatusCodes` — so the server can't tell them apart;
    // it returns both labels' rows whenever either is requested. The disambiguation
    // (by event type — see `matchesStatusFilter`) has to happen client-side. Doing that
    // as a post-filter on a normally-paginated request would desync the displayed rows
    // from the server's page/total (short pages, wrong page count) — see incident where
    // Pending+Approved showed 2/2/10/6 rows across 4 pages instead of full pages. So in
    // this one ambiguous case, fetch the full matching set in one request and paginate
    // the disambiguated result ourselves instead of trusting the server's pagination.
    const isAmbiguous =
      this.appliedStatusFilters.includes(StatusFilterOption.Completed) !==
      this.appliedStatusFilters.includes(StatusFilterOption.Approved);

    const baseFilter = { ...this.params, walletTransactionEvents: events };
    const request = isAmbiguous
      ? this.fetchAllMatching(baseFilter)
      : this.walletService.getWalletTransaction(baseFilter);

    request
      .pipe(
        finalize(() => {
          this.isTransactionListLoading = false;
        }),
      )
      .subscribe({
        next: (data) => {
          if (isAmbiguous) {
            const matched = data.data.filter((item) => this.matchesStatusFilter(item));
            this.total = matched.length;
            const start = (this.params.currentPage - 1) * this.params.take;
            this.transactionList = matched.slice(start, start + this.params.take);
          } else {
            this.transactionList = data.data;
            this.total = data.total;
          }

          if (scrollToView) {
            this.transactionTable?.nativeElement.scrollIntoView({ behavior: 'smooth' });
          }
        },
        error: (err) => {
          this.errorService.showError(err);
        },
      });
  }

  /**
   * Pages through the server in `AMBIGUOUS_FILTER_CHUNK_SIZE` chunks, accumulating
   * every row matching `baseFilter`, until the server's own `total` is fully covered.
   * Used only for the ambiguous Approved-vs-Completed case, where the disambiguation
   * has to run over the complete matching set before it can be re-paginated locally —
   * see `getWalletTransaction`. No fixed cap: an investor with more matching rows
   * than one chunk just costs one extra request per additional chunk.
   */
  private fetchAllMatching(baseFilter: WalletTransactionFilter): Observable<{
    data: TransactionList[];
    total: number;
  }> {
    const take = WalletComponent.AMBIGUOUS_FILTER_CHUNK_SIZE;

    return this.walletService.getWalletTransaction({ ...baseFilter, take, currentPage: 1 }).pipe(
      expand((page, index) =>
        page.data.length === take && (index + 1) * take < page.total
          ? this.walletService.getWalletTransaction({ ...baseFilter, take, currentPage: index + 2 })
          : EMPTY,
      ),
      reduce((acc, page) => ({ data: [...acc.data, ...page.data], total: page.total }), {
        data: [] as TransactionList[],
        total: 0,
      }),
    );
  }

  /**
   * Disambiguates Approved-vs-Completed by event type (trade vs non-trade) for a
   * single row. Only meaningful when exactly one of the two is applied — see the
   * `isAmbiguous` check in `getWalletTransaction`, which is what actually gates when
   * this runs. Non-Approved/FPXSuccessful rows (Pending, Failed, ...) always match.
   */
  private matchesStatusFilter(item: TransactionList): boolean {
    const status = item.walletTransactionHistory?.status;
    const isApprovedFamily =
      status === WalletTransactionStatus.Approved ||
      status === WalletTransactionStatus.FPXSuccessful;
    if (!isApprovedFamily) {
      return true;
    }

    const hasCompleted = this.appliedStatusFilters.includes(StatusFilterOption.Completed);
    const event = item.walletTransactionHistory?.event;
    const isTradeRow = event != null && WalletComponent.TRADE_EVENTS.includes(event);
    return hasCompleted ? isTradeRow : !isTradeRow;
  }

  private resolveStatusCodes(options: StatusFilterOption[]): WalletTransactionStatus[] {
    if (options.length === 0 || options.includes(StatusFilterOption.All)) {
      return [WalletTransactionStatus.All];
    }

    const codes = new Set<WalletTransactionStatus>();
    options.forEach((option) => {
      switch (option) {
        case StatusFilterOption.Pending:
          codes.add(WalletTransactionStatus.PendingApproval);
          codes.add(WalletTransactionStatus.PendingFPXServerResponse);
          break;
        case StatusFilterOption.Approved:
        case StatusFilterOption.Completed:
          codes.add(WalletTransactionStatus.Approved);
          codes.add(WalletTransactionStatus.FPXSuccessful);
          break;
        case StatusFilterOption.Failed:
          codes.add(WalletTransactionStatus.Failed);
          codes.add(WalletTransactionStatus.Cancelled);
          codes.add(WalletTransactionStatus.Declined);
          break;
      }
    });
    return Array.from(codes);
  }

  // ---------------------------------------------------------------------------
  // Desktop "More Filters" dropdown — Event filters section (checkboxes).
  // ---------------------------------------------------------------------------

  isEventFilterSelected(key: WalletTransactionEvent): boolean {
    return this.draftEventFilters.includes(key);
  }

  isAllEventFiltersSelected(): boolean {
    return this.draftEventFilters.includes(WalletTransactionEvent.All);
  }

  toggleEventFilter(key: WalletTransactionEvent): void {
    if (key === WalletTransactionEvent.All) {
      this.draftEventFilters = [WalletTransactionEvent.All];
      return;
    }

    this.draftEventFilters = this.draftEventFilters.filter((k) => k !== WalletTransactionEvent.All);
    this.draftEventFilters = this.draftEventFilters.includes(key)
      ? this.draftEventFilters.filter((k) => k !== key)
      : [...this.draftEventFilters, key];

    if (this.draftEventFilters.length === 0) {
      this.draftEventFilters = [WalletTransactionEvent.All];
    }
  }

  applyEventFilters(): void {
    this.params.walletTransactionEvents = [...this.draftEventFilters];
    this.resetPagination();
  }

  /** Resets the draft only — does NOT re-query (per product decision). */
  clearEventFilters(): void {
    this.draftEventFilters = [WalletTransactionEvent.All];
  }

  // ---------------------------------------------------------------------------
  // Desktop "More Filters" dropdown — Status filters section (checkboxes).
  // ---------------------------------------------------------------------------

  isStatusFilterSelected(key: StatusFilterOption): boolean {
    return this.draftStatusFilters.includes(key);
  }

  isAllStatusFiltersSelected(): boolean {
    return this.draftStatusFilters.includes(StatusFilterOption.All);
  }

  toggleStatusFilter(key: StatusFilterOption): void {
    if (key === StatusFilterOption.All) {
      this.draftStatusFilters = [StatusFilterOption.All];
      return;
    }

    this.draftStatusFilters = this.draftStatusFilters.filter((k) => k !== StatusFilterOption.All);
    this.draftStatusFilters = this.draftStatusFilters.includes(key)
      ? this.draftStatusFilters.filter((k) => k !== key)
      : [...this.draftStatusFilters, key];

    if (this.draftStatusFilters.length === 0) {
      this.draftStatusFilters = [StatusFilterOption.All];
    }
  }

  applyStatusFilters(): void {
    this.appliedStatusFilters = [...this.draftStatusFilters];
    this.params.walletTransactionStatuses = this.resolveStatusCodes(this.appliedStatusFilters);
    this.resetPagination();
  }

  /** Resets the draft only — does NOT re-query (per product decision). */
  clearStatusFilters(): void {
    this.draftStatusFilters = [StatusFilterOption.All];
  }

  isEventFilterApplied(): boolean {
    const selected = this.params.walletTransactionEvents ?? [WalletTransactionEvent.All];
    return selected.length > 0 && !selected.includes(WalletTransactionEvent.All);
  }

  isStatusFilterApplied(): boolean {
    const selected = this.appliedStatusFilters ?? [StatusFilterOption.All];
    return selected.length > 0 && !selected.includes(StatusFilterOption.All);
  }

  /**
   * Mobile: "Event filters" / "Status filters" are two INDEPENDENT NgbDropdowns
   * (each needs its own so it anchors to its own trigger — sharing one ngbDropdown
   * across two toggles is what caused the mis-positioned "flying" panel). Only one
   * may be open at a time, so opening either closes the other.
   */
  onEventFilterDropdownOpenChange(isOpen: boolean, statusFilterDropdown: NgbDropdown): void {
    if (isOpen) {
      statusFilterDropdown.close();
    }
  }

  onStatusFilterDropdownOpenChange(isOpen: boolean, eventFilterDropdown: NgbDropdown): void {
    if (isOpen) {
      eventFilterDropdown.close();
    }
  }

  // Desktop header triggers — mirrors the mobile pair above, but via ViewChild
  // (see the field comment) since the two triggers sit in separate mat-header-cell
  // templates and can't share a local template ref.
  onEventHeaderFilterDropdownOpenChange(isOpen: boolean): void {
    if (isOpen) {
      this.statusHeaderFilterDropdown?.close();
    }
  }

  onStatusHeaderFilterDropdownOpenChange(isOpen: boolean): void {
    if (isOpen) {
      this.eventHeaderFilterDropdown?.close();
    }
  }

  getTransactionStatusTooltip(status: WalletTransactionStatus): string {
    switch (status) {
      case WalletTransactionStatus.Approved:
      case WalletTransactionStatus.FPXSuccessful: // Assuming this is equivalent to 'Successful'
        return 'mysec.transactionSuccessfulStatusTooltip';
      case WalletTransactionStatus.PendingApproval:
      case WalletTransactionStatus.PendingFPXServerResponse: // Assuming these are equivalent to 'Pending'
        return 'mysec.transactionPendingStatusTooltip';
      case WalletTransactionStatus.Cancelled:
        return 'mysec.transactionCancelledStatusTooltip';
      case WalletTransactionStatus.Failed:
        return 'mysec.transactionFailedStatusTooltip';
      case WalletTransactionStatus.Declined:
        return 'mysec.transactionDeclinedStatusTooltip';
      default:
        return '';
    }
  }

  changePage(event?: PageEvent): void {
    if (!event) {
      return;
    }

    this.params.currentPage = event.pageIndex + 1;
    this.params.take = event.pageSize;

    this.getWalletTransaction(true);
  }

  deposit(): void {
    const popupOptions = {
      size: 'md',
      centered: true,
      keyboard: false,
    } as NgbModalOptions;

    const modalRef = this.modalService.open(DepositComponent, popupOptions);

    // Remove the ?topup to avoid being triggered again on refresh
    // this.router.navigate([], {
    //   queryParams: {},
    //   replaceUrl: true,
    // });
    modalRef.result.finally(() => {
      this.router.navigate([], {
        queryParams: {},
        replaceUrl: true,
      });
    });
    modalRef.result.then(
      (amount: number) => {
        this.router.navigate(['/pstx', 'deposit', amount]);
      },
      () => {},
    );
  }

  withdraw(): void {
    const popupOptions = {
      size: 'md',
      centered: true,
      keyboard: false,
    } as NgbModalOptions;

    const modalRef = this.modalService.open(WithdrawComponent, popupOptions);
    // FRC-858: lead the popup with the withdrawable figure (Balance - LockedAmount),
    // not the gross balance. Also pass the hold context so the popup can explain the gap
    // in-place without reproducing the Balance-on-Hold breakdown card.
    modalRef.componentInstance.balance = this.wallet.balance;
    modalRef.componentInstance.withdrawableBalance = this.wallet.withdrawableBalance;
    modalRef.componentInstance.pendingAmount = this.wallet.pendingAmount;
    modalRef.result.finally(() => {
      this.clearWithdrawDeepLinkQueryParam();
    });
    modalRef.result.then(
      () => {
        this.popupService
          .success('mysec.withdrawalMessage', {
            titleLabel: 'mysec.withdrawalInProgress',
            // FRC-858: match the Withdraw/Confirm modal title typography.
            titleClass: 'withdraw-status-title',
            okLabel: 'mysec.backToPSTX',
            cancelLabel: '',
          })
          .then(() => {
            this.getWallet();
          });
      },
      () => {},
    );
  }

  activateWallet(): void {
    this.accountService.profile(true).subscribe((profile) => {
      if ([EkycStatus.Verified, EkycStatus.NotRequired].includes(profile.ekycStatus)) {
        this.router.navigate(['/pstx', 'wallet', 'activate']);
      } else {
        this.popupService.alert('investment.userMustCompleteEkyc').then(() => {
          this.router.navigate(['/proceed-e-kyc']);
        });
      }
    });
  }

  cancelTransaction(walletTransactionHistoryId: number): void {
    this.popupService.confirm('mysec.confirmCancelTransaction').then(
      () => {
        this.blockUiService.open();

        this.walletService
          .cancelTransaction(walletTransactionHistoryId)
          .pipe(finalize(() => this.blockUiService.close()))
          .subscribe({
            next: (data) => {
              this.getWallet();
            },
            error: (err) => {
              this.errorService.showError(err);
            },
          });
      },
      () => {},
    );
  }

  getProfilePaymentMethods(): void {
    this.isLoading = true;

    this.accountService
      .getProfilePaymentMethods()
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.tryOpenWithdrawDeepLink();
        }),
      )
      .subscribe({
        next: (data: any) => {
          this.paymentMethod = data;
        },
        error: (err) => {
          this.errorService.showError(err);
        },
      });
  }

  withdrawValidation(): void {
    if (this.paymentMethod.accountHolder && this.paymentMethod.accountNumber) {
      this.withdraw();
    } else {
      this.popupService.confirm('settings.completePaymentMethodToProceedWithdraw').then(
        () => {
          // INC-284: the withdraw gate re-points to the review-and-attest screen,
          // requiring the bank section before the investor can proceed.
          this.router.navigate(['/profile/review-attest'], {
            queryParams: {
              required: serializeRequiredSections(['bank']),
              returnUrl: this.router.url,
            },
          });
        },
        () => {
          this.clearWithdrawDeepLinkQueryParam();
        },
      );
    }
  }

  getProfileInfo(): void {
    this.accountService.profile(true).subscribe((profile) => {
      const role = this.storgeService.role;

      this.isMaster = role !== Role.CompanyRep || (role === Role.CompanyRep && profile.isMasterRep);
      this.getWallet();
    });
  }

  handleTickerCodeClick() {
    this.onTickerCodeClick.emit(this.tickerCode);
  }

  showOrderSummary(item: TransactionList) {
    const popupOptions = {
      size: 'md',
      centered: true,
      keyboard: false,
      windowClass: 'modal-wrapper--md',
    } as NgbModalOptions;

    const modalRef = this.modalService.open(OrderSummaryComponent, popupOptions);
    modalRef.componentInstance.transaction = item || {};
  }

  /**
   * Trade rows = Buy/Sell PSTX that have settled (Approved / FPXSuccessful).
   * Drives both the "Completed" status label and the "View" (Order Summary) action.
   */
  isCompletedTradeRow(item: TransactionList): boolean {
    const event = item.walletTransactionHistory?.event;
    const status = item.walletTransactionHistory?.status;

    return (
      event != null &&
      status != null &&
      [WalletTransactionEvent.BuyShares, WalletTransactionEvent.SellShares].includes(event) &&
      [WalletTransactionStatus.Approved, WalletTransactionStatus.FPXSuccessful].includes(status)
    );
  }

  /**
   * Event-type label for a transaction row (desktop table + mobile card title).
   * Withdraw reads "Withdrawal" here (noun, matching the filter checkbox and the
   * Withdrawal Requests bucket) — the enum-derived `mysec.Withdraw` key stays
   * "Withdraw" everywhere else (the page's action button, the withdraw dialog).
   */
  eventDisplayLabelKey(event: WalletTransactionEvent | null | undefined): string {
    // The mobile call site passes `walletTransactionHistory?.event`; guard the
    // undefined case so we never emit the literal key `mysec.undefined`.
    if (event == null) {
      return '-';
    }
    return event === WalletTransactionEvent.Withdraw
      ? 'mysec.WithdrawalEvent'
      : 'mysec.' + WalletTransactionEvent[event];
  }

  /**
   * Drives the Cancel action. Withdrawal requests are never cancellable from this
   * table (per product decision) even while PendingApproval — only pending Top Up
   * rows show Cancel.
   */
  isPendingRow(item: TransactionList): boolean {
    return (
      item.walletTransactionHistory?.status === WalletTransactionStatus.PendingApproval &&
      item.walletTransactionHistory?.event !== WalletTransactionEvent.Withdraw
    );
  }

  copyTransactionId(item: TransactionList): void {
    const reference = item.walletTransactionHistory?.referenceNumber;
    if (!reference) return;

    this.clipboard.copy(reference);
    this.toastService.success(this.translateService.instant('common.copiedToClipboard'));
  }

  // ---------------------------------------------------------------------------
  // Mobile transaction-history cards — collapsed by default, click anywhere to
  // expand. Desktop keeps the <mat-table>; this is mobile-only (<1100px).
  // ---------------------------------------------------------------------------

  private readonly expandedTransactionIds = new Set<number>();

  toggleRowExpanded(item: TransactionList): void {
    const id = item.walletTransactionHistory?.id;
    if (id == null) {
      return;
    }
    if (this.expandedTransactionIds.has(id)) {
      this.expandedTransactionIds.delete(id);
    } else {
      this.expandedTransactionIds.add(id);
    }
  }

  isRowExpanded(item: TransactionList): boolean {
    const id = item.walletTransactionHistory?.id;
    return id != null && this.expandedTransactionIds.has(id);
  }

  /** Mirrors OrderSummaryComponent.isBuy — drives the Buy/Sell badge + amount
   *  sign in the mobile card's inline order breakdown (completed trades only). */
  isBuyRow(item: TransactionList): boolean | null {
    const event = item.walletTransactionHistory?.event;
    if (event === WalletTransactionEvent.BuyShares) {
      return true;
    }
    if (event === WalletTransactionEvent.SellShares) {
      return false;
    }
    return null;
  }

  /** Counter for a transaction row: trade rows read the order summary ticker;
   *  ECF investment rows carry the campaign (name + slug) instead. */
  displayCounter(item: TransactionList): string | null {
    return item.orderSummary?.tickerCode ?? item.campaign?.name ?? null;
  }

  /** Link target for a counter: trade rows go to /pstx/:tickerCode, ECF
   *  investment rows go to the campaign page /equity/:slug. */
  counterLink(item: TransactionList): string | null {
    const ticker = item.orderSummary?.tickerCode;
    if (ticker) {
      return '/pstx/' + ticker;
    }
    const slug = item.campaign?.slug;
    return slug ? '/equity/' + slug : null;
  }

  /** Any row with a resolvable counter links to its page; trade rows keep the
   *  ticker-status gating (Dehosting/Inactive stay plain text). */
  isCounterLinkable(item: TransactionList): boolean {
    if (!this.displayCounter(item)) {
      return false;
    }
    if (item.orderSummary?.tickerCode) {
      return ![ShareMarketStatus.Dehosting, ShareMarketStatus.Inactive].includes(
        item.orderSummary?.tickerStatus
      );
    }
    return !!item.campaign?.slug;
  }

  /** Mirrors OrderSummaryComponent.sharesValue. */
  sharesValueFor(item: TransactionList): number {
    const details = item.orderSummary?.orderDetail;
    if (!details?.length) {
      return 0;
    }
    return details.reduce((sum, detail) => sum + (detail?.amount ?? 0), 0);
  }

  // ---------------------------------------------------------------------------
  // #124: Balance on Hold drill-downs
  // ---------------------------------------------------------------------------

  /**
   * A bucket is drillable only when its subtotal is non-zero (zero buckets stay
   * inert — carried over from #123). Drives both the click handler and the
   * chevron/cursor affordance in the template.
   */
  isBucketDrillable(bucket: 'open-orders' | 'withdrawal-requests' | 'ecf-investment'): boolean {
    if (!this.wallet) {
      return false;
    }
    switch (bucket) {
      case 'open-orders':
        return this.wallet.openOrdersHoldAmount > 0;
      case 'withdrawal-requests':
        return this.wallet.withdrawalRequestsHoldAmount > 0;
      case 'ecf-investment':
        return this.wallet.ecfInvestmentHoldAmount > 0;
      default:
        return false;
    }
  }

  private isMobileViewport(): boolean {
    if (!isPlatformBrowser(this.platform)) {
      return false;
    }
    return window.innerWidth < WalletComponent.MOBILE_BREAKPOINT;
  }

  /**
   * #124: pending withdrawals only (Event = Withdraw, Status = PendingApproval,
   * per ADR-0005) — the same predicate the server uses for the bucket subtotal,
   * so the drill-down rows reconcile to withdrawalRequestsHoldAmount.
   */
  filterPendingWithdrawals(rows: TransactionList[]): TransactionList[] {
    return (rows ?? []).filter((item) => {
      const tx = item.walletTransactionHistory;
      return (
        tx?.event === WalletTransactionEvent.Withdraw &&
        tx?.status === WalletTransactionStatus.PendingApproval
      );
    });
  }

  /** Builds the shared view-model for the Open Orders bucket from the #122 data. */
  private buildOpenOrdersBreakdown(orders: HeldOpenOrder[]): HoldBreakdownData {
    return {
      title: 'mysec.openBuyOrdersOnHold',
      countLabel: this.translateService.instant('mysec.ordersCount', {
        count: orders.length,
      }),
      items: orders.map((o) => ({
        primary: (o.tickerCode ?? '').toUpperCase(),
        secondary: o.orderNo,
        amount: o.heldAmount,
      })),
      linkLabel: 'mysec.viewAllOnPstxOrders',
      linkCommands: ['/dashboard', 'orders'],
    };
  }

  /** Builds the shared view-model for the Withdrawal Requests bucket. */
  private buildWithdrawalsBreakdown(rows: TransactionList[]): HoldBreakdownData {
    return {
      title: 'mysec.withdrawalRequestsOnHold',
      countLabel: this.translateService.instant('mysec.requestsCount', {
        count: rows.length,
      }),
      items: rows.map((r) => ({
        primary: r.walletTransactionHistory?.referenceNumber ?? '-',
        secondary: r.walletTransactionHistory?.createdAt
          ? this.formatDate(r.walletTransactionHistory.createdAt)
          : '',
        amount: Math.abs(r.walletTransactionHistory?.amount ?? 0),
      })),
      linkLabel: 'mysec.viewInTransactionHistory',
      // The transaction-history table IS this PSTX Account page — a router
      // navigate to the same URL is a no-op, so the link instead filters the
      // table to what this bucket represents (Withdraw + Pending, ADR-0005)
      // and scrolls to it.
      linkCommands: [],
      linkAction: () => this.viewPendingWithdrawalsInHistory(),
    };
  }

  /**
   * Withdrawal Requests bucket → "View in transaction history": filters the
   * table to Event = Withdrawal, Status = Pending (the same predicate the
   * bucket subtotal itself uses, per ADR-0005) and scrolls it into view.
   */
  private viewPendingWithdrawalsInHistory(): void {
    this.draftEventFilters = [WalletTransactionEvent.Withdraw];
    this.draftStatusFilters = [StatusFilterOption.Pending];
    this.params.walletTransactionEvents = [...this.draftEventFilters];
    this.appliedStatusFilters = [...this.draftStatusFilters];
    this.params.walletTransactionStatuses = this.resolveStatusCodes(this.appliedStatusFilters);
    this.resetPagination();
  }

  private formatDate(value: string): string {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) {
      return '';
    }
    return d.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }

  /**
   * #124: responsive opener for the Open Orders bucket. Lazily fetches the #122
   * data, then routes to the bottom sheet (mobile) or the anchored popover
   * (desktop). `popover` is the desktop NgbPopover instance bound to the row.
   */
  openOpenOrdersBreakdown(popover?: NgbPopover): void {
    if (!this.isBucketDrillable('open-orders')) {
      return;
    }

    // Re-click on an already-open popover just toggles it closed — no refetch.
    if (popover?.isOpen()) {
      popover.close();
      return;
    }

    if (this.heldOpenOrders) {
      this.presentOpenOrders(this.heldOpenOrders, popover);
      return;
    }

    this.isHeldOpenOrdersLoading = true;
    this.walletService
      .getHeldOpenOrders()
      .pipe(finalize(() => (this.isHeldOpenOrdersLoading = false)))
      .subscribe({
        next: (data) => {
          this.heldOpenOrders = data.orders;
          this.presentOpenOrders(this.heldOpenOrders, popover);
        },
        error: (err) => {
          this.errorService.showError(err);
        },
      });
  }

  private presentOpenOrders(orders: HeldOpenOrder[], popover?: NgbPopover): void {
    const data = this.buildOpenOrdersBreakdown(orders);
    if (this.isMobileViewport()) {
      this.openSheet(data);
    } else {
      this.openPopover(data, popover);
    }
  }

  /** Builds the shared view-model for the ECF investment bucket. */
  private buildEcfInvestmentsBreakdown(investments: HeldEcfInvestment[]): HoldBreakdownData {
    return {
      title: 'mysec.ecfInvestmentsOnHold',
      countLabel: this.translateService.instant('mysec.investmentsCount', {
        count: investments.length,
      }),
      items: investments.map((i) => ({
        primary: i.campaignName ?? '-',
        secondary: i.paidAt
          ? this.translateService.instant('mysec.ecfPaidOn', { date: this.formatDate(i.paidAt) })
          : '-',
        amount: i.amount,
      })),
      linkLabel: 'mysec.viewAllInEcfInvestments',
      // The ECF Investments tab is a different route, so navigate to it with the
      // Paid filter deep-link. Use linkAction (not linkCommands) so the
      // queryParams ride along; the shared content dismisses after it fires.
      linkCommands: [],
      linkAction: () =>
        this.router.navigate(['/dashboard', 'pledges'], {
          queryParams: { payment: 'Paid' },
        }),
    };
  }

  /**
   * Responsive opener for the ECF investment bucket. Lazily fetches the
   * heldEcfInvestments list (reconciles to ecfInvestmentHoldAmount server-side),
   * then routes to the bottom sheet (mobile) or the anchored popover (desktop).
   */
  openEcfInvestmentsBreakdown(popover?: NgbPopover): void {
    if (!this.isBucketDrillable('ecf-investment')) {
      return;
    }

    // Re-click on an already-open popover just toggles it closed — no refetch.
    if (popover?.isOpen()) {
      popover.close();
      return;
    }

    if (this.heldEcfInvestments) {
      this.presentEcfInvestments(this.heldEcfInvestments, popover);
      return;
    }

    this.isHeldEcfInvestmentsLoading = true;
    this.walletService
      .getHeldEcfInvestments()
      .pipe(finalize(() => (this.isHeldEcfInvestmentsLoading = false)))
      .subscribe({
        next: (data) => {
          this.heldEcfInvestments = data.investments;
          this.presentEcfInvestments(this.heldEcfInvestments, popover);
        },
        error: (err) => {
          this.errorService.showError(err);
        },
      });
  }

  private presentEcfInvestments(investments: HeldEcfInvestment[], popover?: NgbPopover): void {
    const data = this.buildEcfInvestmentsBreakdown(investments);
    if (this.isMobileViewport()) {
      this.openSheet(data);
    } else {
      this.openPopover(data, popover);
    }
  }

  /**
   * #124: responsive opener for the Withdrawal Requests bucket. Fetches pending
   * withdrawals with a DEDICATED filter (not the table's `params`) so the
   * drill-down rows reconcile to the bucket subtotal regardless of the table's
   * current page or user-applied filters.
   */
  openWithdrawalsBreakdown(popover?: NgbPopover): void {
    if (!this.isBucketDrillable('withdrawal-requests')) {
      return;
    }

    // Re-click on an already-open popover just toggles it closed — no refetch.
    if (popover?.isOpen()) {
      popover.close();
      return;
    }

    const filter = new WalletTransactionFilter();
    // Pending withdrawals are rare, so one large page covers the bucket; the
    // history endpoint has no event filter, so narrow by status server-side
    // and by event client-side.
    filter.take = 100;
    filter.walletTransactionStatuses = [WalletTransactionStatus.PendingApproval];

    this.isPendingWithdrawalsLoading = true;
    this.walletService
      .getWalletTransaction(filter)
      .pipe(finalize(() => (this.isPendingWithdrawalsLoading = false)))
      .subscribe({
        next: (result) => {
          const rows = this.filterPendingWithdrawals(result.data);
          const data = this.buildWithdrawalsBreakdown(rows);
          if (this.isMobileViewport()) {
            this.openSheet(data);
          } else {
            this.openPopover(data, popover);
          }
        },
        error: (err) => {
          this.errorService.showError(err);
        },
      });
  }

  /** Desktop: feed the row's anchored popover and open it (toggle if re-clicked). */
  private openPopover(data: HoldBreakdownData, popover?: NgbPopover): void {
    if (!popover) {
      return;
    }
    if (popover.isOpen()) {
      popover.close();
      return;
    }
    // Only one bucket popover open at a time.
    this.closeBreakdownPopovers();
    this.activeBreakdown = data;
    popover.open({ data });
  }

  /** Mobile: open the bottom-sheet-styled modal hosting the shared content. */
  private openSheet(data: HoldBreakdownData): void {
    const modalRef = this.modalService.open(HoldBreakdownSheetComponent, {
      windowClass: 'hold-breakdown-sheet-modal',
      backdropClass: 'hold-breakdown-sheet-backdrop',
      scrollable: false,
      animation: true,
    } as NgbModalOptions);
    modalRef.componentInstance.data = data;
  }

  /** Desktop popover link click: navigate then close any open bucket popover. */
  onBreakdownNavigate(commands: any[]): void {
    if (commands?.length) {
      this.router.navigate(commands);
    }
    this.closeBreakdownPopovers();
  }

  /** Closes whichever bucket popover is open (in-popover × control). */
  closeBreakdownPopovers(): void {
    this.holdPopovers?.forEach((p) => {
      if (p.isOpen()) {
        p.close();
      }
    });
  }
}
